import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { parseISO, format, getYear, getMonth } from 'date-fns';
import { TrendingUp, DollarSign, ChevronDown, ChevronUp } from 'lucide-react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function getRate(customer, unitType) {
  if (unitType === 'Tons') return parseFloat(customer.tonRate || 0);
  if (unitType === 'Loads') return parseFloat(customer.loadRate || 0);
  return parseFloat(customer.hourlyRate || 0);
}

function getAmount(log, customer) {
  const qty = parseFloat(log.quantity || log.hours || 0);
  const rate = getRate(customer, log.unitType || 'Hours');
  return qty * rate;
}

export default function Reports() {
  const { logs, customers, payments } = useApp();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [expanded, setExpanded] = useState(null);

  // Get all available years from logs
  const years = [...new Set(logs.map(l => {
    try { return getYear(parseISO(l.date)); } catch { return null; }
  }).filter(Boolean))].sort((a, b) => b - a);

  if (!years.includes(selectedYear) && years.length > 0) {
    years.push(selectedYear);
    years.sort((a, b) => b - a);
  }

  // Filter logs for selected year
  const yearLogs = logs.filter(l => {
    try { return getYear(parseISO(l.date)) === selectedYear; }
    catch { return false; }
  });

  // Build customer summaries
  const customerSummaries = customers.map(c => {
    const cLogs = yearLogs.filter(l => l.customerId === c.id);
    const totalInvoiced = cLogs.reduce((sum, l) => sum + getAmount(l, c), 0);

    // Get payments for this customer this year
    const cPayments = payments.filter(p => {
      if (p.customerId !== c.id) return false;
      try {
        const weekKey = p.weekKey || '';
        return weekKey.startsWith(String(selectedYear));
      } catch { return false; }
    });
    const totalPaid = cPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const outstanding = totalInvoiced - totalPaid;

    // Monthly breakdown
    const monthly = MONTHS.map((month, idx) => {
      const monthLogs = cLogs.filter(l => {
        try { return getMonth(parseISO(l.date)) === idx; }
        catch { return false; }
      });
      const amount = monthLogs.reduce((sum, l) => sum + getAmount(l, c), 0);
      return { month, amount, count: monthLogs.length };
    });

    return { ...c, cLogs, totalInvoiced, totalPaid, outstanding, monthly };
  }).filter(c => c.totalInvoiced > 0).sort((a, b) => b.totalInvoiced - a.totalInvoiced);

  // Grand totals
  const grandInvoiced = customerSummaries.reduce((sum, c) => sum + c.totalInvoiced, 0);
  const grandPaid = customerSummaries.reduce((sum, c) => sum + c.totalPaid, 0);
  const grandOutstanding = customerSummaries.reduce((sum, c) => sum + c.outstanding, 0);

  // Monthly grand totals
  const monthlyTotals = MONTHS.map((month, idx) => {
    const amount = customerSummaries.reduce((sum, c) => sum + (c.monthly[idx]?.amount || 0), 0);
    return { month, amount };
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ fontSize: 20 }}>Annual Report</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 0 }}>Year</label>
          <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))} style={{ width: 100 }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid-3" style={{ marginBottom: 20 }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, fontFamily: 'Barlow Condensed', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: 8 }}>Total Invoiced</div>
          <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 800, fontSize: 28, color: 'var(--accent)' }}>${grandInvoiced.toFixed(2)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, fontFamily: 'Barlow Condensed', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: 8 }}>Total Collected</div>
          <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 800, fontSize: 28, color: 'var(--success)' }}>${grandPaid.toFixed(2)}</div>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, fontFamily: 'Barlow Condensed', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: 8 }}>Outstanding</div>
          <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 800, fontSize: 28, color: grandOutstanding > 0 ? 'var(--danger)' : 'var(--success)' }}>${grandOutstanding.toFixed(2)}</div>
        </div>
      </div>

      {/* Monthly Totals Bar Chart */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>Monthly Revenue — {selectedYear}</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 120 }}>
          {monthlyTotals.map(({ month, amount }) => {
            const maxAmount = Math.max(...monthlyTotals.map(m => m.amount), 1);
            const height = Math.max((amount / maxAmount) * 100, amount > 0 ? 4 : 0);
            return (
              <div key={month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ fontSize: 9, color: 'var(--text-dim)', fontFamily: 'Barlow Condensed' }}>
                  {amount > 0 ? `$${(amount / 1000).toFixed(1)}k` : ''}
                </div>
                <div style={{ width: '100%', height: `${height}px`, background: amount > 0 ? 'var(--accent)' : 'var(--border)', borderRadius: '3px 3px 0 0', minHeight: amount > 0 ? 4 : 2, transition: 'height 0.3s' }} />
                <div style={{ fontSize: 9, color: 'var(--text-muted)', fontFamily: 'Barlow Condensed', fontWeight: 600 }}>{month}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Customer Breakdown */}
      {customerSummaries.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <TrendingUp size={32} color="var(--text-dim)" style={{ marginBottom: 10 }} />
          <p style={{ color: 'var(--text-muted)' }}>No invoices found for {selectedYear}.</p>
        </div>
      ) : (
        <div>
          <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, color: 'var(--text-muted)' }}>By Customer</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {customerSummaries.map(c => (
              <div key={c.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Header */}
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', cursor: 'pointer', flexWrap: 'wrap' }}
                  onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 16 }}>{c.name}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                      {c.cLogs.length} log{c.cLogs.length !== 1 ? 's' : ''}
                      {c.totalPaid > 0 && <> · Paid: <span style={{ color: 'var(--success)' }}>${c.totalPaid.toFixed(2)}</span></>}
                      {c.outstanding > 0 && <> · Due: <span style={{ color: 'var(--danger)' }}>${c.outstanding.toFixed(2)}</span></>}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 800, fontSize: 22, color: 'var(--accent)' }}>${c.totalInvoiced.toFixed(2)}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-dim)', fontFamily: 'Barlow Condensed' }}>TOTAL {selectedYear}</div>
                  </div>
                  {expanded === c.id ? <ChevronUp size={16} color="var(--text-muted)" /> : <ChevronDown size={16} color="var(--text-muted)" />}
                </div>

                {/* Monthly breakdown */}
                {expanded === c.id && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '14px 18px', background: 'var(--bg3)' }}>
                    <div style={{ fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: 12 }}>Monthly Breakdown</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ color: 'var(--text-dim)', fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                          <th style={{ textAlign: 'left', paddingBottom: 6 }}>Month</th>
                          <th style={{ textAlign: 'right', paddingBottom: 6 }}>Logs</th>
                          <th style={{ textAlign: 'right', paddingBottom: 6 }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.monthly.filter(m => m.amount > 0).map(m => (
                          <tr key={m.month} style={{ borderTop: '1px solid var(--border)' }}>
                            <td style={{ padding: '7px 0' }}>{m.month} {selectedYear}</td>
                            <td style={{ padding: '7px 0', textAlign: 'right', color: 'var(--text-muted)' }}>{m.count}</td>
                            <td style={{ padding: '7px 0', textAlign: 'right', color: 'var(--success)', fontFamily: 'Barlow Condensed', fontWeight: 600 }}>${m.amount.toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr style={{ borderTop: '2px solid var(--border)' }}>
                          <td style={{ padding: '8px 0', fontFamily: 'Barlow Condensed', fontWeight: 700 }}>Total</td>
                          <td style={{ padding: '8px 0', textAlign: 'right', color: 'var(--text-muted)', fontFamily: 'Barlow Condensed', fontWeight: 700 }}>{c.cLogs.length}</td>
                          <td style={{ padding: '8px 0', textAlign: 'right', color: 'var(--accent)', fontFamily: 'Barlow Condensed', fontWeight: 800, fontSize: 15 }}>${c.totalInvoiced.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
